import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ButtonModule } from 'primeng/button';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminService } from '../../../../services/admin.service';
import { finalize } from 'rxjs';
import { NotificationService } from '../../../../services/notification.service';
import { NumericOnlyDirective } from '../../../sharedComponents/directives';

interface RadioOption {
  label: string;
  value: string;
  actionCode: string; // Added property to store backend action code
  disabled?: boolean;
}

@Component({
    selector: 'app-tlr-data-editor-permanent-repair-date',
    imports: [
        CommonModule,
        FormsModule,
        TableModule,
        RadioButtonModule,
        ButtonModule,
        NumericOnlyDirective,
    ],
    providers: [],
    templateUrl: './tlr-data-editor-permanent-repair-date.component.html',
    styleUrl: './tlr-data-editor-permanent-repair-date.component.scss'
})
export class TlrDataEditorPermanentRepairDateComponent implements OnInit {
  // Radio button options with corresponding backend action codes
  radioOptions: RadioOption[] = [
    { label: 'AIR', value: 'air', actionCode: 'AIR', disabled: false },
    { label: 'Operations', value: 'operations', actionCode: 'OP', disabled: false },
    { label: 'Inspections', value: 'inspections', actionCode: 'INSP', disabled: false },
    { label: 'OA', value: 'oa', actionCode: 'OA', disabled: false },
    { label: 'Engineering', value: 'engineering', actionCode: 'ENG', disabled: false },
    { label: 'TLR Permanent Repair Date', value: 'tlr-permanent-repair-date', actionCode: 'PER', disabled: false }
  ];
  
  // Selected option (default to air)
  selectedOption: string = 'air';
  
  // Tag number input
  tagNumber: string = '';
  
  // Header text for the component
  headerText: string = 'Edit AIR';
  
  // Current mode
  currentMode: string = 'permanent-data-editor';

  // Loading state
  isLoading: boolean = false;
  responseMessage: any;
  
  constructor(
    private route: ActivatedRoute, 
    private router: Router,
    private adminService: AdminService,
    private notificationService:NotificationService
  ) {
    // Force component recreation on navigation by subscribing to router events
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }
    ngOnInit(): void {
    // Get the mode from route data
    this.currentMode = this.route.snapshot.data['mode'];
    
    if (this.currentMode === 'permanent-repair') {
      // In permanent-repair mode, only TLR Permanent Repair Date should be selectable
      this.selectedOption = 'tlr-permanent-repair-date';
      this.updateHeaderText(this.selectedOption);
      
      // Disable all other options
      this.radioOptions.forEach(option => {
        option.disabled = option.value !== 'tlr-permanent-repair-date';
      });
    } else {
      // Default to AIR for 'permanent-data-editor' mode
      this.selectedOption = 'air';
      this.updateHeaderText(this.selectedOption);
      
      // Enable all options
      this.radioOptions.forEach(option => {
        option.disabled = false;
      });
    }
    
  }
    onRadioChange(value: string): void {
    // If in permanent-repair mode, prevent changing from tlr-permanent-repair-date
    if (this.currentMode === 'permanent-repair' && value !== 'tlr-permanent-repair-date') {
      this.selectedOption = 'tlr-permanent-repair-date';
      return;
    }
    
    this.selectedOption = value;
    
    // Update header text based on selected option
    this.updateHeaderText(value);
  }
  
  // Helper method to update header text based on selected value
  private updateHeaderText(selectedValue: string): void {
    switch(selectedValue) {
      case 'air':
        this.headerText = 'Edit AIR';
        break;
      case 'operations':
        this.headerText = 'Edit Operations';
        break;
      case 'inspections':
        this.headerText = 'Edit Inspections';
        break;
      case 'oa':
        this.headerText = 'Edit OA';
        break;
      case 'engineering':
        this.headerText = 'Edit Engineering';
        break;
      case 'tlr-permanent-repair-date':
        this.headerText = 'Edit TLR Permanent Repair Date';
        break;
      default:
        this.headerText = 'Edit AIR';
        break;
    }
  }
  
  // Helper method to get the backend action code for the selected option
  getSelectedActionCode(): string {
    const selectedRadioOption = this.radioOptions.find(option => option.value === this.selectedOption);
    return selectedRadioOption ? selectedRadioOption.actionCode : 'AIR'; // Default to AIR if not found
  }
  
  onGoClick(): void {
    if (!this.tagNumber) {
      this.notificationService.showError('Please enter a tag number');
      return;
    }
    
    // Set loading state
    this.isLoading = true;
    
    // Call validateTagNumberOperator to check if tag number is valid
    this.adminService.validateTagNumberOperator({
      tagNumber: this.tagNumber,
      action: this.getSelectedActionCode() // Use the correct action code for the backend
    })
    .pipe(
      finalize(() => {
        this.isLoading = false;
      })
    )
    .subscribe(response => {
     
      if (!response.recordFound) {
        this.responseMessage = response.message;
        // Show error message
        // this.messageService.add({
        //   severity: 'error',
        //   summary: 'Invalid Tag Number',
        //   detail: response.message || 'The tag number is invalid for the selected action.'
        // });
        return;
      }
      
      // If validation successful, proceed with navigation
      switch (this.selectedOption) {        
        case 'air':
          this.router.navigate(['/admin/other-functions/data-editor/air'], { 
            queryParams: { tagNumber: this.tagNumber } 
          });
          break;
        case 'operations':
          this.router.navigate(['/admin/other-functions/data-editor/operations'], { 
            queryParams: { tagNumber: this.tagNumber.toString().trim() } 
          });
          break;
        case 'inspections':
          // Navigate to inspections editor
          this.router.navigate(['/admin/other-functions/data-editor/inspector'], { 
            queryParams: { tagNumber: this.tagNumber.toString().trim() } 
          });
          break;        case 'oa':
          // Navigate to OA details editor
          this.router.navigate(['/admin/other-functions/data-editor/oa-details'], { 
            queryParams: { tagNumber: this.tagNumber.toString().trim() } 
          });
          break;
        case 'engineering':
          // Navigate to engineering editor
          this.router.navigate(['/admin/other-functions/data-editor/engineering-details'], { 
            queryParams: { tagNumber: this.tagNumber.toString().trim() } 
          });
          break;
        case 'tlr-permanent-repair-date':
          // Navigate to permanent repair date editor
          this.router.navigate(['/admin/other-functions/data-editor/oa-details'], { 
            queryParams: { tagNumber: this.tagNumber.toString().trim() } 
          });
          break;
        default:
          console.error('Unknown option selected:', this.selectedOption);
      }
    });
  }
}
